import App, { removeServiceTemplateId } from '@/Common/Runtime/AppsCollector/App';
import { host } from '@/Common/Runtime';
import { MESSAGE_TYPES } from '@/Common/Runtime/events';
import { ux1Data } from '@/Common/Utils/General/Modules';
import Vue from 'vue';

function transformVueRouteToUIRT(viewName: string): string {
	// Summary
	// 	 transforms Vue route to UIRT viewId
	//   ex.: sample-vue-app-MainView to http://www.parallels.com/sample-vue-app#main-view
	const tmpViewName = viewName.split('-').pop();
	return ux1Data.changeCase.paramCase(tmpViewName || '');
}

function transformUIRTRouteToVue(viewName: string): string {
	// Summary
	// 	 transforms UIRT viewId to Vue route
	//   ex.: http://www.parallels.com/sample-vue-app#main-view to sample-vue-app-MainView
	const routeData = (viewName.split('/').pop() as string).split('#');
	const [appId, route] = routeData;

	return `${appId}-${ux1Data.changeCase.pascalCase(route)}`;
}

const getPackageId = (url: string): string => url.split('/')[4];

function getEvent(event: Event): { type: MESSAGE_TYPES; data: object } {
	// We always send only one parameter
	const e = event as CustomEvent;
	return (e.detail && e.detail.length && e.detail[0]) || {};
}

function callNavigation({
	route = '',
	payload = {} as APS.INavigationTreeItem
}: {
	route: string;
	payload: APS.INavigationTreeItem;
}) {
	if (route) {
		const newRoute = route.includes('/') ? route : transformVueRouteToUIRT(route);
		window.aps.apsc.gotoView(newRoute, null, payload);
	} else {
		// if 'route' is empty trying to check if payload is instance of APS.INavigationTreeItem
		if (payload && payload.id && payload.type && payload.type === 'navigation') {
			window.aps.navigation.doNavigation && window.aps.navigation.doNavigation(payload);
		} else {
			// eslint-disable-next-line no-console
			console.warn('In case of empty route, payload type must be APS.INavigationTreeItem.');
		}
	}
}

function getOptionsFromScratch(context: Partial<APS.IApsContext>, appId: string, devMode: boolean) {
	const { src: url } = context;
	const packageId = getPackageId(url as string);
	const mashupArea = document.querySelector('#aps2MashupArea') as HTMLElement;

	const parent = document.createElement('div');

	parent.setAttribute('id', appId);
	parent.setAttribute('ccp-container', 'true');

	mashupArea.appendChild(parent);

	const homeRoute = undefined;
	const options = { homeRoute, packageId, context, baseApiUrl: '', stylesheetUrls: [] } as Host.IOptionsCreateApp;

	return { url, appId, parent, devMode, options } as Host.ICreateInstanceAppParams;
}

function getOptionsFromNav(data: ADAPTER.IData, devMode: boolean) {
	const context = window.aps.context as never as APS.IApsContext;
	const packageId = getPackageId(context._view.src);
	const { url, appId, parent, viewId, payload } = data || {};
	const homeRoute = { route: transformUIRTRouteToVue(viewId), payload };
	const options = { homeRoute, packageId, context };

	return { url, appId, parent, devMode, options };
}

function createInstanceApp({
	appId,
	data,
	dontRender = false
}: {
	appId: string;
	data: ADAPTER.IData;
	dontRender: boolean;
}) {
	return Promise.resolve().then(() => {
		const app = host.getApp(appId);
		const { devMode = false } = window.aps;
		return (
			app ||
			Promise.resolve()
				.then(() => {
					return dontRender
						? window.aps.apsc
								.loadPackage(appId.replace('vue_', ''))
								.then((context: APS.IApsContext) => getOptionsFromScratch(context, appId, devMode))
						: getOptionsFromNav(data, devMode);
				})
				.then((options) => host.createInstanceApp(options))
				.catch((e) => {
					// eslint-disable-next-line no-console
					console.error("'createInstanceApp': Error created application!: ", e, '; App Id: ', appId);

					return null;
				})
				.then((application) => {
					application?.addListener((e: Event) => handleEventApp(e, application));

					return application;
				})
		);
	});
}

function callShareComponent(data: ADAPTER.IData, currentApp: App) {
	const { appId: _appId, componentName } = data || {};
	// OA-21625: allow navigation between dojo and vue screens
	const appId = `vue_${_appId}`;

	Promise.resolve()
		.then(() => {
			return createInstanceApp({ appId, data, dontRender: true }).then((app) => {
				const result = {
					isError: false
				} as ADAPTER.IResult;

				if (app) {
					const view = app.getComponent(componentName as string);

					if (view) {
						const appName = appId.split('/').reverse()[0];
						const realComponentName = `${appName}${componentName}`;
						/* eslint-disable @typescript-eslint/no-explicit-any */
						const component = (Vue as any).options.components[realComponentName];

						!component && Vue.component(realComponentName, view);

						result.realComponentName = realComponentName;
					} else {
						// eslint-disable-next-line no-console
						console.warn(
							"'callShareComponent': the requested component was not found: component name - ",
							componentName,
							' App Id: ',
							_appId
						);
						result.isError = true;
					}
				} else {
					result.isError = true;
				}

				return result;
			});
		})
		.catch((e) => {
			// eslint-disable-next-line no-console
			console.error(
				"'callShareComponent': Error sharing component:",
				e,
				'; Component name - ',
				componentName,
				'; App Id: ',
				_appId
			);

			return { isError: true } as ADAPTER.IResult;
		})
		.then(({ isError = false, realComponentName }) => {
			const payload = {
				requestedAppId: _appId,
				requestedComponentName: componentName,
				realComponentName,
				isError
			};

			currentApp.emit({
				type: MESSAGE_TYPES.SHARE_COMPONENT,
				payload
			} as App.IPayloadData);
		});
}

function callAnalytics(data: Analytics.ITypedData) {
	window.aps.apsc.pushData(data);
}

function callNotification(data: APS.INotification, app: App) {
	// eslint-disable-next-line no-console
	console.debug(`Notification from Vue application | appId: ${app.id}`, data);
	//@ts-ignore
	window.aps.apsc.injectMessage(data.message, data.type);
}

function handleEventApp(e: Event, app: App) {
	const { NAVIGATION, ANALYTICS, NOTIFICATION, SHARE_COMPONENT } = MESSAGE_TYPES;
	const callsEvents = {
		[NAVIGATION]: callNavigation,
		[ANALYTICS]: callAnalytics,
		[NOTIFICATION]: callNotification,
		[SHARE_COMPONENT]: callShareComponent
	};
	const { type, data } = getEvent(e);

	/* eslint-disable @typescript-eslint/no-explicit-any */
	callsEvents[type] && callsEvents[type](data as any, app);
}

const createVueApp = (data: ADAPTER.IData): Promise<void> => {
	const { appId, isCreated, prevApp } = data || {};

	// eslint-disable-next-line no-console
	console.debug(`Creating Vue application | appId: ${appId}`);

	if (prevApp && !prevApp.isVue) {
		prevApp.iframe.style.display = 'none';
	}

	return createInstanceApp({ appId, data, dontRender: false }).then((app) => {
		if (app) {
			isCreated?.resolve();
			window.aps.apsc.ready();
			app.activate();
		}
	});
};

const navigation = (data: { viewName: string; appId?: string; context?: APS.IApsContext }): void => {
	const { appId, context = {} as APS.IApsContext } = data || {};
	const app = host.getApp(appId || '');
	const { _view: view } = context;

	let viewName!: string;
	// fix for OA-34602, remove serviceTemplateId from view.id
	const { viewId, serviceTemplateId } = removeServiceTemplateId(view.id);
	if (serviceTemplateId) {
		viewName = `${transformUIRTRouteToVue(viewId)}/${view.serviceTemplateId}`;
	} else {
		viewName = transformUIRTRouteToVue(viewId);
	}

	// eslint-disable-next-line no-console
	console.debug(`Emitting navigation event to Vue app | appId: ${appId}, viewId: ${viewName}`);

	const event = {
		type: '',
		data: {
			viewName,
			context,
			payload: context.params
		}
	};

	app?.emitNavigation(event);
};

const getAppReady = (appId: string): boolean => {
	const app = host.getApp(appId);

	return (app && app.isReady()) || false;
};

export { createVueApp, navigation, getAppReady };
